// Time Report Export Utilities (CSV and PDF)
import { format } from 'date-fns';
import { TimeLog, StaffReport } from '../types/timeReport';
import { parseUTCTimestamp } from './dateUtils';
import { formatHours } from './timeReportCalculations';

/**
 * Generate CSV content from time logs
 */
export const generateTimeReportCSV = (logs: TimeLog[], staffName?: string): string => {
  const headers = [
    'Staff Member',
    'Role',
    'Date',
    'Clock In',
    'Clock Out',
    'Hours Worked',
    'Status',
    'Admin Edited',
    'Admin Notes',
  ];

  const rows = logs.map((log) => {
    const clockIn = parseUTCTimestamp(log.clock_in_time);
    const clockOut = log.clock_out_time ? parseUTCTimestamp(log.clock_out_time) : null;

    return [
      log.staff_name || staffName || '',
      log.staff_role || '',
      format(clockIn, 'yyyy-MM-dd'),
      format(clockIn, 'h:mm a'),
      clockOut ? format(clockOut, 'h:mm a') : 'Incomplete',
      (log.hours_worked || 1.0).toFixed(2),
      log.status,
      log.admin_edited ? 'Yes' : 'No',
      log.admin_notes || '',
    ];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  return csvContent;
};

/**
 * Generate CSV content from staff reports (summary format)
 */
export const generateStaffReportCSV = (reports: StaffReport[]): string => {
  const headers = [
    'Staff Member',
    'Role',
    'Total Hours',
    'Days Worked',
    'Average Hours/Day',
    'Total Shifts',
    'Incomplete Shifts',
    'Status',
  ];

  const rows = reports.map((report) => [
    report.staff_name,
    report.staff_role,
    report.total_hours.toFixed(2),
    report.days_worked.toString(),
    report.avg_hours.toFixed(2),
    report.shift_count.toString(),
    report.incomplete_count.toString(),
    report.incomplete_count === 0 ? 'Complete' : 'Needs Review',
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  return csvContent;
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate PDF using jsPDF
 * Note: This is a placeholder that will be implemented after installing jspdf
 */
export const generateTimeReportPDF = async (
  logs: TimeLog[],
  staffName: string,
  startDate: Date,
  endDate: Date
): Promise<void> => {
  // Dynamic import to avoid loading jspdf if not needed
  const jsPDF = (await import('jspdf')).default;
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF();

  // Header
  doc.setFontSize(18);
  doc.text('Lotus Healing Spa - Time Report', 14, 20);

  doc.setFontSize(11);
  doc.text(`Staff: ${staffName}`, 14, 28);
  doc.text(
    `Period: ${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`,
    14,
    34
  );
  doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy h:mm a')}`, 14, 40);

  // Calculate summary
  const totalHours = logs.reduce((sum, log) => sum + (log.hours_worked || 1.0), 0);
  const daysWorked = new Set(
    logs.map((log) => format(parseUTCTimestamp(log.clock_in_time), 'yyyy-MM-dd'))
  ).size;
  const avgHours = daysWorked > 0 ? totalHours / daysWorked : 0;
  const incompleteCount = logs.filter((log) => log.needs_review).length;

  // Summary Stats
  doc.setFontSize(12);
  doc.text('Summary', 14, 50);
  doc.setFontSize(10);
  doc.text(`Total Hours: ${formatHours(totalHours)}`, 14, 56);
  doc.text(`Days Worked: ${daysWorked}`, 14, 62);
  doc.text(`Average: ${formatHours(avgHours)}/day`, 14, 68);
  doc.text(`Incomplete Shifts: ${incompleteCount}`, 14, 74);

  // Table data
  const tableData = logs.map((log) => {
    const clockIn = parseUTCTimestamp(log.clock_in_time);
    const clockOut = log.clock_out_time ? parseUTCTimestamp(log.clock_out_time) : null;

    return [
      format(clockIn, 'MM/dd/yyyy'),
      format(clockIn, 'h:mm a'),
      clockOut ? format(clockOut, 'h:mm a') : 'N/A',
      formatHours(log.hours_worked || 1.0),
      log.needs_review ? '⚠' : log.admin_edited ? '✏' : '✓',
    ];
  });

  // @ts-ignore - autoTable adds to jsPDF prototype
  autoTable(doc, {
    startY: 82,
    head: [['Date', 'Clock In', 'Clock Out', 'Hours', 'Status']],
    body: tableData,
    theme: 'striped',
    styles: { fontSize: 9 },
    headStyles: { fillColor: [58, 94, 99] }, // Brand color #3A5E63
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 30 },
      2: { cellWidth: 30 },
      3: { cellWidth: 25 },
      4: { cellWidth: 20, halign: 'center' },
    },
  });

  // Footer with legend
  // @ts-ignore
  const finalY = doc.lastAutoTable.finalY || 82;
  doc.setFontSize(8);
  doc.text('Status Legend: ✓ = Complete | ⚠ = Incomplete | ✏ = Admin Edited', 14, finalY + 10);

  // Page numbers
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  // Save
  const filename = `TimeReport_${staffName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(filename);
};

/**
 * Generate PDF for staff summary report
 */
export const generateStaffSummaryPDF = async (
  reports: StaffReport[],
  startDate: Date,
  endDate: Date
): Promise<void> => {
  const jsPDF = (await import('jspdf')).default;
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF();

  // Header
  doc.setFontSize(18);
  doc.text('Lotus Healing Spa - Staff Summary Report', 14, 20);

  doc.setFontSize(11);
  doc.text(
    `Period: ${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`,
    14,
    28
  );
  doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy h:mm a')}`, 14, 34);

  // Calculate overall summary
  const totalHours = reports.reduce((sum, r) => sum + r.total_hours, 0);
  const totalStaff = reports.length;

  doc.setFontSize(12);
  doc.text('Overall Summary', 14, 44);
  doc.setFontSize(10);
  doc.text(`Total Staff: ${totalStaff}`, 14, 50);
  doc.text(`Combined Hours: ${formatHours(totalHours)}`, 14, 56);

  // Table data
  const tableData = reports.map((report) => [
    report.staff_name,
    report.staff_role,
    formatHours(report.total_hours),
    report.days_worked.toString(),
    formatHours(report.avg_hours),
    report.shift_count.toString(),
    report.incomplete_count > 0 ? report.incomplete_count.toString() : '-',
  ]);

  // @ts-ignore
  autoTable(doc, {
    startY: 64,
    head: [['Staff', 'Role', 'Total Hours', 'Days', 'Avg/Day', 'Shifts', 'Incomplete']],
    body: tableData,
    theme: 'striped',
    styles: { fontSize: 9 },
    headStyles: { fillColor: [58, 94, 99] },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 35 },
      2: { cellWidth: 25, halign: 'right' },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 25, halign: 'right' },
      5: { cellWidth: 20, halign: 'center' },
      6: { cellWidth: 25, halign: 'center' },
    },
  });

  // Page numbers
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  // Save
  const filename = `StaffSummary_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(filename);
};
